import * as React from 'react';
import { Loader, ExpandableGroup, Toggle, Tooltip } from '@teamsnap/teamsnap-ui';

import { GroupEligibilityRow } from './types';
import DatetimePicker from 'components/form/date-time-picker';

import './GroupEligibility.scss';
import MultiSelectPicker from 'frontend-toolkit/src/components/MultiSelectPicker/MultiSelectPicker';
import ENVIRONMENT from '../../../core/environment';
import { DateFormat, DateService, FeatureFlagService } from 'frontend-toolkit';
import { sortByName } from 'core/utils';
import { Checkbox, Icon, Select } from '@teamsnap/snap-ui';
import { FeatureFlagConfig, FeatureFlags } from 'core/feature-flags';

type Props = {
  genders: string[];
  rows: GroupEligibilityRow[];
  setRowsCallback: React.Dispatch<React.SetStateAction<GroupEligibilityRow[]>>;
  orgId: number;
};

const GroupEligibility = ({ genders, rows, setRowsCallback, orgId }: Props) => {
  const autoRosteringIsEnabled = FeatureFlagService.isFeatureEnabledForOrg(
    FeatureFlagConfig[FeatureFlags.AUTO_ROSTERING],
    ENVIRONMENT,
    orgId
  );

  const groupRefs = rows?.sort(sortByName)?.map(() => React.createRef<HTMLDivElement>());

  React.useEffect(() => {
    if (rows?.length > 0) {
      const errorIndex = rows.findIndex((row) => row.active && row.autoRostering.error);

      if (errorIndex > -1) {
        setTimeout(() => {
          groupRefs[errorIndex]?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 0);
      }
    }
  }, [rows]);

  const handleToggle = (rowId: number, type: string) => {
    // Find the index of the targeted row
    const targetIndex = rows.findIndex((row) => row.id === rowId);
    // Make a copy since we should not try to change a passed param
    const rowsCopy = [...rows];

    // Toggle the value at the proper index
    rowsCopy[targetIndex][type].toggled = !rowsCopy[targetIndex][type].toggled;

    // Update parent state
    setRowsCallback(rowsCopy);
  };

  const handleDateChange = (rowId, date, dateType) => {
    // Find the index of the targeted row
    const targetIndex = rows.findIndex((row) => row.id === rowId);
    const rowsCopy = [...rows];
    rowsCopy[targetIndex].birthdates[dateType] = date ? DateService.dateToString(date, DateFormat.YYYY_MM_DD) : null;

    setRowsCallback(rowsCopy);
  };

  const handleMultiSelectUpdate = (rowId: number, type: string, values: string[]) => {
    // Find the index of the targeted row
    const targetIndex = rows.findIndex((row) => row.id === rowId);
    // Make a copy since we should not try to change a passed param
    const rowsCopy = [...rows];

    if (type === 'genders') {
      // Toggle the value at the proper index
      rowsCopy[targetIndex].genders.options = values;
    }

    // Update parent state
    setRowsCallback(rowsCopy);
  };

  const handleSingleSelectUpdate = (rowId: number, type: string, value: any) => {
    // Find the index of the targeted row
    const targetIndex = rows.findIndex((row) => row.id === rowId);
    // Make a copy since we should not try to change a passed param
    const rowsCopy = [...rows];

    if (type === 'autoRostering') {
      // Toggle the value at the proper index
      const isDivision = rowsCopy[targetIndex].autoRostering.options
        .find((option) => option.label === 'Divisions')
        .options.some((x) => x.value === value);

      const rosterModeValue = isDivision ? 'instant' : rowsCopy[targetIndex].autoRostering.rosterMode ?? 'instant';

      rowsCopy[targetIndex].autoRostering.rosterScopeId = value;
      rowsCopy[targetIndex].autoRostering.rosterScopeType = isDivision ? 'division' : 'team';
      rowsCopy[targetIndex].autoRostering.rosterMode = rosterModeValue; // Default to instant until user interacts with the checkbox unless an interaction already occurred.
      rowsCopy[targetIndex].autoRostering.error = null;
    }

    // Update parent state
    setRowsCallback(rowsCopy);
  };

  const handleAutoRosterCheckbox = (rowId: number, checked: boolean) => {
    // Find the index of the targeted row
    const targetIndex = rows.findIndex((row) => row.id === rowId);
    // Make a copy since we should not try to change a passed param
    const rowsCopy = [...rows];

    // Toggle the value at the proper index
    rowsCopy[targetIndex].autoRostering.rosterMode = checked ? 'builder' : 'instant';

    // Update parent state
    setRowsCallback(rowsCopy);
  };

  const setExpandedCallback = (rowId: number) => {
    // Find the index of the targeted row
    const targetIndex = rows.findIndex((row) => row.id === rowId);
    // Make a copy since we should not try to change a passed param
    const rowsCopy = [...rows];

    // rowsCopy[targetIndex].error = null;
    rowsCopy[targetIndex].expanded = !rows[targetIndex].expanded;

    // Update parent state
    setRowsCallback(rowsCopy);
  };

  if (!rows || rows?.length === 0) {
    return <Loader type="spin" text="Loading" />;
  }

  return (
    <div className="group-eligibility" data-testid="GroupEligibility">
      {rows
        ?.filter((r) => r.active)
        ?.sort(sortByName)
        ?.map((row, index) => (
          <ExpandableGroup
            isExpanded={row.expanded}
            key={`${row.name.split(' ').join('-')}-${row.id}`}
            label={row.name}
            onExpandedChange={() => setExpandedCallback(row.id)}
            testId={`GroupEligibility--container_${row.name}`}
          >
            <div ref={groupRefs[index]} className="group-eligibility__container">
              <div className="group-eligibility__row">
                <div className="group-eligibility__toggle sui-mr-1">
                  <Toggle
                    name={`birthdates-${row.id}`}
                    inputProps={{
                      checked: row.birthdates.toggled,
                      onChange: () => handleToggle(row.id, 'birthdates'),
                    }}
                  />

                  <div className="u-spaceLeftSm">Birthdates</div>
                </div>

                {row.birthdates.toggled ? (
                  <>
                    <DatetimePicker
                      id={`birthdates-${row.id}-to`}
                      datetime={
                        row.birthdates?.start
                          ? DateService.stringToDate(row.birthdates.start, DateFormat.YYYY_MM_DD)
                          : null
                      }
                      onChange={(date) => handleDateChange(row.id, date, 'start')}
                      showTimeSelect={false}
                      dateFormat="P"
                      startDate={new Date('01/01/1900')}
                      endDate={
                        row.birthdates.end ? DateService.stringToDate(row.birthdates.end, DateFormat.YYYY_MM_DD) : null
                      }
                      placeholderText="MM/ DD / YYYY"
                    />

                    <div style={{ padding: '0 8px' }}>to</div>

                    <DatetimePicker
                      id={`birthdates-${row.id}-from`}
                      datetime={
                        row.birthdates?.end ? DateService.stringToDate(row.birthdates.end, DateFormat.YYYY_MM_DD) : null
                      }
                      onChange={(date) => handleDateChange(row.id, date, 'end')}
                      showTimeSelect={false}
                      dateFormat="P"
                      startDate={
                        row.birthdates?.start
                          ? DateService.stringToDate(row.birthdates.start, DateFormat.YYYY_MM_DD)
                          : new Date('01/01/1900')
                      }
                      placeholderText="MM/ DD / YYYY"
                      filterDate={(date) => (date ? date > new Date(row.birthdates?.start) : null)}
                    />
                  </>
                ) : null}
              </div>

              <div className="group-eligibility__row">
                <div className="group-eligibility__toggle sui-mr-1">
                  <Toggle
                    name={`genders-${row.id}`}
                    inputProps={{
                      checked: row.genders?.toggled,
                      onChange: () => handleToggle(row.id, 'genders'),
                    }}
                  />

                  <div className="u-spaceLeftSm">Gender</div>
                </div>

                {row.genders?.toggled ? (
                  <MultiSelectPicker
                    options={genders.map((x) => ({ label: x, value: x }))}
                    updateSelectedOptions={(selected) => handleMultiSelectUpdate(row.id, 'genders', selected)}
                    selected={row.genders.options}
                  />
                ) : null}
              </div>

              {autoRosteringIsEnabled && (
                <div className="group-eligibility__row sui-items-start">
                  <div className="sui-flex sui-mt-1">
                    <Toggle
                      name={`autoRoster-${row.id}`}
                      inputProps={{
                        checked: row.autoRostering?.toggled,
                        onChange: () => handleToggle(row.id, 'autoRostering'),
                      }}
                    />

                    <div className="u-spaceLeftSm sui-flex sui-ml-1 sui-mr-0.5">
                      Auto-Roster{' '}
                      <Tooltip text="Once registrants complete their forms and submit, they will be automatically rostered to the destinations you have specified (excludes waitlist).">
                        <Icon className="sui-ml-0.5 sui-text-blue-40" name="info" />
                      </Tooltip>
                    </div>
                  </div>

                  {row.autoRostering?.toggled ? (
                    <div className="sui-flex-1">
                      <div className="sui-mb-1">
                        <Select
                          onChange={(e) => handleSingleSelectUpdate(row.id, 'autoRostering', e)}
                          name={`autoRoster-${row.id}-dropdown`}
                          options={row.autoRostering?.options}
                          value={row?.autoRostering?.rosterScopeId?.toString()}
                          isSearchable={true}
                          errors={row.autoRostering?.error ? [row.autoRostering?.error] : null}
                        />
                      </div>

                      {row.autoRostering?.rosterScopeType === 'team' && (
                        <Checkbox
                          onChange={(e) => handleAutoRosterCheckbox(row.id, e.target.checked)}
                          label="Assign roster profiles to this team as 'Pending'. Pending roster assignments are not visible to teams until published."
                          extraProps={{
                            input: {
                              checked: row.autoRostering?.rosterMode === 'builder',
                              id: `autoRoster-${row.id}-checkbox`,
                            },
                          }}
                        />
                      )}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </ExpandableGroup>
        ))}
    </div>
  );
};

export default GroupEligibility;
